import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { tagsAPI, threadsAPI } from '../services/api';
import './PracticeSession.css';

const QUESTION_COUNT_OPTIONS = [5, 10];

const buildSessionName = (tagName, totalQuestions) => `${totalQuestions}-question ${tagName} practice`;

const PracticeSession = ({ threadId, tagId, onThreadCreated }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedQuestionCount, setSelectedQuestionCount] = useState(10);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentThread, setCurrentThread] = useState(null);
  const [timer, setTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const [pausedTime, setPausedTime] = useState(0);
  const [isSessionCompleted, setIsSessionCompleted] = useState(false);
  const pendingResponseRef = useRef(null);
  const startedSessionKeyRef = useRef(null);

  const requestedQuestionCount = (() => {
    const questionCount = Number(new URLSearchParams(location.search).get('questions'));
    return QUESTION_COUNT_OPTIONS.includes(questionCount) ? questionCount : 10;
  })();

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  // Monitor session completion and stop timer
  useEffect(() => {
    if (currentThread && currentThread.status === 'completed') {
      setIsSessionCompleted(true);
      pauseTimer(); // Stop the timer when session is completed
    }
  }, [currentThread]);


  // Timer functions
  const startTimer = () => {
    setTimer(0);
    setPausedTime(0);
    setIsTimerPaused(false);
    const startTime = Date.now();
    const interval = setInterval(() => {
      setTimer(prev => {
        const elapsed = (Date.now() - startTime) / 1000; // Convert to seconds with decimal precision
        const newTime = Math.round(elapsed * 10) / 10; // Round to 1 decimal place
        return newTime;
      });
    }, 100); // Update every 100ms for better precision
    setTimerInterval(interval);
  };

  const pauseTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setIsTimerPaused(true);
    setPausedTime(timer);
  };

  const stopTimer = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
    setIsTimerPaused(false);
  };

  const resetTimer = () => {
    stopTimer();
    setTimer(0);
    setPausedTime(0);
    setIsTimerPaused(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = (seconds % 60).toFixed(1); // Show 1 decimal place for seconds
    return `${mins}:${secs.padStart(4, '0')}`; // Pad to 4 chars to include decimal
  };

  const loadTags = async () => {
    try {
      const response = await tagsAPI.getAll();
      setTags(response.results || []);
    } catch (error) {
      console.error('Error loading tags:', error);
    }
  };

  const loadExistingThread = useCallback(async (threadId) => {
    try {
      setIsLoading(true);
      
      // Load thread details
      const threadData = await threadsAPI.getById(threadId);
      setCurrentThread(threadData);
      setSessionStarted(true);
      
      // Start timer for the current question
      startTimer();
      
      // For new threads, check if this is a brand new thread by looking at progress
      // If current_question is 1 and there's an initial_ai_response, it's likely a new thread
      const isNewThread = threadData.progress.current_question === 1 && threadData.initial_ai_response;
      
      if (isNewThread) {
        // For new threads, only use the initial AI response
        console.log('Loading new thread - using initial_ai_response only');
        const initialMessage = {
          id: 0, // Use array index 0 for first message
          type: 'ai',
          content: threadData.initial_ai_response,
          timestamp: new Date()
        };
        setMessages([initialMessage]);
      } else {
        // For existing threads, try to load conversation history
        console.log('Loading existing thread - fetching conversation history');
        let conversationData;
        try {
          conversationData = await threadsAPI.getConversation(threadId);
        } catch (error) {
          console.error('Error loading conversation:', error);
          conversationData = { messages: [] };
        }
        
        // Check if there are actual conversation messages
        if (conversationData.messages && conversationData.messages.length > 0) {
        // Convert conversation history to messages format
        const conversationMessages = conversationData.messages.map((msg, index) => ({
          id: index, // Use array index as ID (0, 1, 2, 3...)
          type: msg.type === 'human' ? 'user' : 'ai',
          content: msg.content,
          timestamp: new Date()
        }));
          
          setMessages(conversationMessages);
        } else {
          // Fallback to initial AI response if no conversation history
          const initialMessage = {
            id: 0, // Use array index 0 for first message
            type: 'ai',
            content: threadData.initial_ai_response || `Welcome to your ${threadData.tag.name} practice session! Let's start practicing your lines.`,
            timestamp: new Date()
          };
          setMessages([initialMessage]);
        }
      }
      
    } catch (error) {
      console.error('Error loading thread:', error);
      // Redirect back to practice if thread doesn't exist
      navigate('/');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  // Load data on component mount
  useEffect(() => {
    loadTags();
    
    // If we have a threadId, load the existing thread
    if (threadId) {
      loadExistingThread(threadId);
    }
  }, [threadId, loadExistingThread]);

  // Handle tagId after tags are loaded
  useEffect(() => {
    if (!tagId || tags.length === 0 || threadId) {
      return;
    }

    const parsedTagId = parseInt(tagId, 10);
    if (Number.isNaN(parsedTagId)) {
      return;
    }

    const sessionKey = `${parsedTagId}:${requestedQuestionCount}`;
    if (startedSessionKeyRef.current === sessionKey) {
      return;
    }

    startedSessionKeyRef.current = sessionKey;
    setSelectedTags([parsedTagId]);
    startSessionWithTag(parsedTagId, requestedQuestionCount);
  }, [tagId, tags, threadId, requestedQuestionCount]);

  // Handle pending response updates
  useEffect(() => {
    if (pendingResponseRef.current) {
      const { response, isError } = pendingResponseRef.current;
      
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        updated[lastIndex] = {
          ...updated[lastIndex],
          content: isError 
            ? 'Sorry, there was an error processing your response. Please try again.'
            : response.response.ai_feedback,
          isLoading: false
        };
        return updated;
      });
      
      if (!isError) {
        setCurrentThread(response.session);
      }
      
      pendingResponseRef.current = null;
      setIsLoading(false);
    }
  }, [pendingResponseRef.current]);

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const startSessionWithTag = async (tagId, totalQuestions = 10) => {
    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      alert('Session creation is taking longer than expected. Please try again.');
    }, 30000); // 30 second timeout

    try {
      setIsLoading(true);
      
      // Auto-generate session name based on selected tag
      const selectedTag = tags.find(tag => tag.id === tagId);
      if (!selectedTag) {
        throw new Error(`Tag with ID ${tagId} not found`);
      }
      const sessionName = buildSessionName(selectedTag.name, totalQuestions);
      
      console.log('Creating thread with:', {
        tagId: tagId,
        sessionName: sessionName,
        description: ''
      });
      
      // Create a new thread with the selected tag
      const threadData = await threadsAPI.create(
        tagId,
        sessionName,
        '', // Empty description
        totalQuestions
      );
      
      console.log('Thread created successfully:', threadData);
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);
      
      // Don't set messages here - let loadExistingThread handle it when we navigate
      // This prevents duplicate messages
      setSessionStarted(true);
      
      // Start timer for first question
      startTimer();
      
      // Refresh the threads list in the parent component
      if (onThreadCreated) {
        onThreadCreated();
      }
      
      // Check if thread_id exists before navigating
      if (threadData.thread_id) {
        // Navigate to the new thread - this will trigger loadExistingThread
        navigate(`/practice/${threadData.thread_id}`);
      } else {
        console.error('Thread created but no thread_id returned:', threadData);
        alert('Session created but there was an issue. Please try refreshing the page.');
      }
      
    } catch (error) {
      // Clear the timeout since we got an error
      clearTimeout(timeoutId);
      
      console.error('Error creating thread:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to create session. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const startSession = async () => {
    if (selectedTags.length === 0) {
      alert('Please select at least one tag to practice');
      return;
    }

    // Set a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      alert('Session creation is taking longer than expected. Please try again.');
    }, 30000); // 30 second timeout

    try {
      setIsLoading(true);
      
      // Auto-generate session name based on selected tag
      const selectedTag = tags.find(tag => tag.id === selectedTags[0]);
      const sessionName = buildSessionName(selectedTag.name, selectedQuestionCount);
      
      console.log('Creating thread with:', {
        tagId: selectedTags[0],
        sessionName: sessionName,
        description: ''
      });
      
      // Create a new thread with the first selected tag
      const threadData = await threadsAPI.create(
        selectedTags[0], // Use first selected tag
        sessionName,
        '', // Empty description
        selectedQuestionCount
      );
      
      console.log('Thread created successfully:', threadData);
      
      // Clear the timeout since we got a response
      clearTimeout(timeoutId);
      
      // Don't set messages here - let loadExistingThread handle it when we navigate
      // This prevents duplicate messages
      setSessionStarted(true);
      
      // Start timer for first question
      startTimer();
      
      // Refresh the threads list in the parent component
      if (onThreadCreated) {
        onThreadCreated();
      }
      
      // Check if thread_id exists before navigating
      if (threadData.thread_id) {
        // Navigate to the new thread - this will trigger loadExistingThread
        navigate(`/practice/${threadData.thread_id}`);
      } else {
        console.error('Thread created but no thread_id returned:', threadData);
        alert('Session created but there was an issue. Please try refreshing the page.');
      }
      
    } catch (error) {
      // Clear the timeout since we got an error
      clearTimeout(timeoutId);
      
      console.error('Error creating thread:', error);
      console.error('Error details:', error.response?.data || error.message);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to create session. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim() || !currentThread || isSessionCompleted) return;

    const messageToSend = currentMessage;
    const responseTime = timer; // Capture current timer value
    setCurrentMessage('');
    stopTimer(); // Stop the timer when sending

    // Add user message and loading message
    setMessages(prev => {
      const userMessage = {
        id: prev.length, // Next array index for user message
        type: 'user',
        content: messageToSend,
        timestamp: new Date()
      };

      const loadingMessage = {
        id: prev.length + 1, // Next array index for loading message
        type: 'ai',
        content: null,
        timestamp: new Date(),
        isLoading: true
      };

      return [...prev, userMessage, loadingMessage];
    });

    try {
      const response = await threadsAPI.respond(currentThread.thread_id, messageToSend, responseTime);
      
      // Update the last message (loading message) with the actual response
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        updated[lastIndex] = {
          ...updated[lastIndex],
          content: response.response.ai_feedback,
          isLoading: false
        };
        return updated;
      });
      
      setCurrentThread(response.session);
      
      // Start timer for next question
      startTimer();
      
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Update the last message (loading message) with error
      setMessages(prev => {
        const updated = [...prev];
        const lastIndex = updated.length - 1;
        updated[lastIndex] = {
          ...updated[lastIndex],
          content: 'Sorry, there was an error processing your response. Please try again.',
          isLoading: false
        };
        return updated;
      });
    } finally {
      // Loading state is handled through message array, not global loading state
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };


  // Show loading state when loading an existing thread
  if (isLoading && threadId) {
    return (
      <div className="practice-session">
        <div className="chat-container">
          <div className="chat-header">
            <div className="header-left">
              <button 
                className="back-btn"
                onClick={() => navigate('/practice')}
                title="Back to Practice Games"
              >
                ← Back
              </button>
              <h3>Loading Session...</h3>
            </div>
          </div>

          <div className="chat-messages">
            <div className="message ai loading">
              <div className="message-content">
                <div className="pulse-bubble"></div>
              </div>
            </div>
          </div>

          <div className="chat-input">
            <textarea
              disabled={true}
              placeholder="Loading session..."
              rows={3}
            />
            <button 
              className="btn btn-primary send-btn"
              disabled={true}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state when loading tags for tagId
  if (tagId && tags.length === 0 && !sessionStarted) {
    return (
      <div className="practice-session">
        <div className="chat-container">
          <div className="chat-header">
            <div className="header-left">
              <button 
                className="back-btn"
                onClick={() => navigate('/practice')}
                title="Back to Practice Games"
              >
                ← Back
              </button>
              <h3>Loading Tags...</h3>
            </div>
          </div>

          <div className="chat-messages">
            <div className="message ai loading">
              <div className="message-content">
                <div className="pulse-bubble"></div>
              </div>
            </div>
          </div>

          <div className="chat-input">
            <textarea
              disabled={true}
              placeholder="Loading tags..."
              rows={3}
            />
            <button 
              className="btn btn-primary send-btn"
              disabled={true}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show setup screen for new sessions or when no thread is loaded
  if (!sessionStarted && !threadId) {
    // If loading, show chat container with loading bubble
    if (isLoading) {
      return (
        <div className="practice-session">
          <div className="chat-container">
            <div className="chat-header">
              <div className="header-left">
                <button 
                  className="back-btn"
                  onClick={() => navigate('/practice')}
                  title="Back to Practice Games"
                >
                  ← Back
                </button>
                <h3>Creating Practice Session...</h3>
              </div>
            </div>

            <div className="chat-messages">
              <div className="message ai loading">
                <div className="message-content">
                  <div className="pulse-bubble"></div>
                </div>
              </div>
            </div>

            <div className="chat-input">
              <textarea
                disabled={true}
                placeholder="Creating your session..."
                rows={3}
              />
              <button 
                className="btn btn-primary send-btn"
                disabled={true}
              >
                Send
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="practice-session">
        <div className="session-setup">
          <h3>Start a Practice Session</h3>
          <p>Select the tags you want to practice and start your AI-powered session!</p>
          
          <div className="tag-selection">
            <h4>Select Tags to Practice:</h4>
            <div className="tag-grid">
              {tags.map(tag => (
                <button
                  key={tag.id}
                  className={`tag-option ${selectedTags.includes(tag.id) ? 'selected' : ''}`}
                  onClick={() => handleTagToggle(tag.id)}
                  disabled={isLoading}
                >
                  <span className="tag-name">{tag.name}</span>
                  <span className="tag-count">{tag.line_count} lines</span>
                </button>
              ))}
            </div>
          </div>

          <div className="tag-selection">
            <h4>Choose Drill Length:</h4>
            <div className="tag-grid">
              {QUESTION_COUNT_OPTIONS.map(questionCount => (
                <button
                  key={questionCount}
                  className={`tag-option ${selectedQuestionCount === questionCount ? 'selected' : ''}`}
                  onClick={() => setSelectedQuestionCount(questionCount)}
                  disabled={isLoading}
                >
                  <span className="tag-name">{questionCount} Questions</span>
                  <span className="tag-count">{questionCount === 5 ? 'Short drill' : 'Standard drill'}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="session-actions">
            <button 
              className="btn btn-primary start-session-btn"
              onClick={startSession}
              disabled={selectedTags.length === 0 || isLoading}
            >
              {isLoading ? 'Creating Session...' : 'Start Practice Session'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="practice-session">
      <div className="chat-container">
        <div className="chat-header">
          <div className="header-left">
            <button 
              className="back-btn"
              onClick={() => navigate('/practice')}
              title="Back to Practice Games"
            >
              ← Back
            </button>
            <h3>{currentThread ? currentThread.thread_name : 'Practice Session'}</h3>
          </div>
          <div className="session-info">
            {currentThread && (
              <>
                <span>Tag: {currentThread.tag.name}</span>
                <span>Status: {currentThread.status}</span>
              </>
            )}
            <button className="btn btn-secondary end-session-btn" onClick={() => navigate('/practice')}>
              End Session
            </button>
          </div>
        </div>

        <div className="chat-messages">
          {messages.map(message => {
            const isLoading = message.isLoading === true;
            return (
              <div key={message.id} className={`message ${message.type} ${isLoading ? 'loading' : ''}`}>
                <div className="message-content">
                  {isLoading ? (
                    <div className="pulse-bubble"></div>
                  ) : (
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  )}
                </div>
                <div className="message-time">
                  {message.timestamp.toLocaleTimeString()}
                </div>
              </div>
            );
          })}
        </div>

        <div className="chat-input">
          <div className="timer-and-progress">
            <div className={`timer-display ${isTimerPaused ? 'timer-paused' : ''} ${isSessionCompleted ? 'timer-completed' : ''}`}>
              <span className="timer-label">Time:</span>
              <span className="timer-value">{formatTime(timer)}</span>
              {isSessionCompleted && <span className="timer-status">SESSION COMPLETED</span>}
            </div>
            {currentThread && (
              <div className="progress-display">
                <span className="progress-label">Question:</span>
                <span className="progress-value">{currentThread.progress.current_question}/{currentThread.progress.total_questions}</span>
              </div>
            )}
          </div>
          <textarea
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isSessionCompleted ? "Session completed - no more responses needed" : "Type your response here..."}
            rows={3}
            disabled={isLoading || isSessionCompleted}
          />
          <button 
            className="btn btn-primary send-btn"
            onClick={sendMessage}
            disabled={!currentMessage.trim() || isLoading || isSessionCompleted}
          >
            {isSessionCompleted ? "Session Completed" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PracticeSession;
